import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "hanukkah-gift-giving-guide",
  "title": "Hanukkah Gift-Giving: Modern Guide to the Festival of Lights",
  "excerpt": "Eight nights, endless possibilities. Learn the traditions, modern practices, and thoughtful gift ideas for Hanukkah celebrations.",
  "date": "2024-12-10",
  "readTime": "6 min read",
  "category": "Festivals",
  "image": "🕎",
  "heroImage": "https://images.unsplash.com/photo-1608452964553-9b4d97b2752f?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Hanukkah Gift-Giving: Modern Guide to the Festival of Lights\n\nHanukkah, the eight-day Jewish celebration, has evolved to include gift-giving traditions that honor both ancient customs and modern practices.\n\n## The History\n\nTraditionally, Hanukkah focused on religious observance rather than gifts. The modern practice of giving gifts on each of the eight nights was influenced by proximity to Christmas celebrations.\n\n## Traditional Hanukkah Gifts\n\n**Gelt**: Chocolate coins wrapped in gold foil, representing the traditional monetary gifts given to children.\n\n**Dreidels**: The four-sided spinning top used in traditional Hanukkah games.\n\n**Books**: Especially those about Jewish history, culture, or children's Hanukkah stories.\n\n## Modern Gift Approaches\n\n**The Eight Nights Strategy**:\n- Nights 1-2: Small gifts (candy, gelt, dreidels)\n- Nights 3-5: Medium gifts (books, games, toys)\n- Nights 6-7: Experiences (tickets, outings)\n- Night 8: The \"big\" gift\n\n**One Gift Per Night**: Rotating between practical, fun, educational, and charitable themes.\n\n**Combination Approach**: A few special nights with gifts, others with experiences or acts of tzedakah (charity).\n\n## Organizing a Hanukkah Gift Exchange\n\nFor extended family gatherings:\n\n- Consider drawing names for one special gift rather than everyone buying for everyone\n- Set a budget of $30-50 per person\n- Include a \"white elephant\" style game with a Hanukkah theme\n- Exchange on the most convenient night (often the weekend night)\n\n## Meaningful Gift Ideas\n\n- **Judaica**: Menorahs, mezuzahs, or decorative items\n- **Experience Gifts**: Museum memberships, concert tickets, cooking classes\n- **Charitable Donations**: In the recipient's name to causes they care about\n- **Handmade Items**: Art, baked goods, or crafts\n- **Family Time**: Coupons for activities together\n\n## Conclusion\n\nHanukkah gift-giving is about bringing light and joy into the darkest time of year. Whether you choose traditional or modern approaches, focus on thoughtfulness and the spirit of the celebration."
};

export default function HanukkahGiftGivingGuidePage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
